import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bell, Check, AlertCircle, Smartphone } from 'lucide-react';
import { api, ApiError } from '@/api';
import LocaleSwitch from '@shared/ui/LocaleSwitch.jsx';

const LOGO = '/logos/logo.svg';

/**
 * Where every App Store button leads while the iOS app is in Apple's review.
 *
 * Someone already registered (signed in on this browser, or arriving from the
 * App Store button in their ticket email, which carries a signed `p`) gets one
 * button. Anyone else gives an address and the same privacy consent as at
 * registration. When the CMS marks iOS as released this page forwards to the
 * store instead, so old links keep working.
 */
export default function IosSoon() {
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState(null);
  const [mode, setMode] = useState('loading'); // loading | known | anonymous
  const [knownEmail, setKnownEmail] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState('idle'); // idle | sending | done
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // The signed link from the ticket email, taken out of the address bar so a
    // shared or bookmarked URL does not carry it around.
    const params = new URLSearchParams(window.location.search);
    const p = params.get('p');
    if (p) window.history.replaceState({}, '', window.location.pathname);

    (async () => {
      const settings = await api.event.settings().catch(() => null);
      if (cancelled) return;
      setEvent(settings);
      if (settings?.ios_released && settings?.store?.ios) {
        window.location.replace(settings.store.ios);
        return;
      }

      // Registered on this browser: the session from registration is kept.
      const { access } = await api.readTokens();
      if (access) {
        const me = await api.participants.me().catch(() => null);
        if (cancelled) return;
        if (me?.email) {
          setKnownEmail(me.email);
          setMode('known');
          return;
        }
      }

      if (p) {
        const check = await api.request(`/ios-waitlist/link?p=${encodeURIComponent(p)}`, { auth: false }).catch(() => null);
        if (cancelled) return;
        if (check?.valid) {
          setLinkToken(p);
          setMode('known');
          return;
        }
      }

      setMode('anonymous');
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setError(null);

    const body = { locale: i18n.resolvedLanguage };
    if (mode === 'known') {
      if (linkToken) body.p = linkToken;
    } else {
      if (!consent) {
        setError(t('register.consentRequired'));
        return;
      }
      body.email = email.trim().toLowerCase();
      body.consent = true;
    }

    setState('sending');
    try {
      const result = await api.request('/ios-waitlist', { method: 'POST', body, auth: mode === 'known' && !linkToken });
      if (result?.status === 'released' && result.url) {
        window.location.replace(result.url);
        return;
      }
      setState('done');
    } catch (err) {
      setState('idle');
      if (err instanceof ApiError && err.code === 'invalid_email') setError(t('iosSoon.invalidEmail'));
      else if (err instanceof ApiError && err.code === 'consent_required') setError(t('register.consentRequired'));
      else setError(t('common.error'));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-black border-b border-white/10">
        <div className="h-1 w-full bg-brand" />
        <div className="site-shell px-5 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-sm label-tag transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t('microsite.backHome')}
          </Link>
          <LocaleSwitch />
        </div>
      </header>

      <main className="site-shell px-5 py-10 max-w-[38rem]">
        <img src={LOGO} alt="The Monday Festival" className="h-8 w-auto mb-8" />

        <div className="flex items-center gap-2 mb-3">
          <Smartphone className="w-5 h-5 text-brand" aria-hidden="true" />
          <span className="label-tag text-brand text-[11px]">{t('iosSoon.tag')}</span>
        </div>
        <h1 className="display-lg text-white text-4xl sm:text-5xl mb-4">{t('iosSoon.title')}</h1>
        <p className="text-white/70 text-base leading-relaxed font-body mb-8">{t('iosSoon.body')}</p>

        {mode === 'loading' ? (
          <div className="py-8 flex justify-center">
            <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        ) : state === 'done' ? (
          <div className="rounded-xl border border-brand/40 bg-brand/5 px-5 py-6" role="status">
            <p className="flex items-center gap-2 font-heading font-bold uppercase text-brand text-lg">
              <Check className="w-5 h-5" /> {t('iosSoon.doneTitle')}
            </p>
            <p className="text-white/60 text-sm leading-relaxed font-body mt-2">{t('iosSoon.doneBody')}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === 'known' ? (
              knownEmail && (
                <p className="text-white/50 text-sm font-body">
                  {t('iosSoon.knownAs')} <span className="text-white break-all">{knownEmail}</span>
                </p>
              )
            ) : (
              <>
                <div>
                  <label className="label-tag text-white/40 text-[10px] block mb-2" htmlFor="email">
                    {t('register.email')} *
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-base
                               font-body focus:outline-none focus:border-brand/50 placeholder:text-white/20"
                  />
                </div>
                <label className="flex gap-3 items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 shrink-0 accent-[#abffc1]"
                  />
                  <span className="text-white/50 text-xs leading-relaxed font-body">
                    {t('register.consent')}{' '}
                    <Link to="/privacy" className="text-brand underline underline-offset-2">
                      {t('microsite.privacyLink')}
                    </Link>
                  </span>
                </label>
              </>
            )}

            {error && (
              <div role="alert" className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-destructive text-xs font-body">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={state === 'sending' || (mode === 'anonymous' && !consent)}
              className="w-full py-4 rounded-xl bg-brand text-black font-heading font-black uppercase text-xl
                         disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2
                         hover:brightness-95 transition"
            >
              {state === 'sending' ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Bell className="w-5 h-5" /> {t('iosSoon.notify')}
                </>
              )}
            </button>

            {mode === 'anonymous' && (
              <p className="text-white/40 text-xs font-body">
                {t('iosSoon.notRegistered')}{' '}
                <Link to="/#register" className="text-brand underline underline-offset-2">
                  {t('iosSoon.registerLink')}
                </Link>
              </p>
            )}
          </form>
        )}

        {event?.store?.android && (
          <div className="mt-12 pt-6 border-t border-white/10">
            <p className="text-white/50 text-sm font-body mb-3">{t('iosSoon.android')}</p>
            <a href={event.store.android} aria-label={t('microsite.googlePlayLabel')}>
              <img src="/buttons/google-play.png" alt={t('microsite.googlePlayLabel')} className="h-11 w-auto hover:opacity-85 transition" />
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
